<template>
  <template v-if="report">
    <section class="rounded-lg bg-background p-4">
      <div class="mb-4 flex flex-col items-center">
        <span
          class="inline-flex items-center rounded-full border border-background-fg/20 bg-background-fg/6 px-2 py-px text-[0.625rem] font-semibold uppercase tracking-wide text-background-fg/75"
        >
          Z Report
        </span>
        <p
          class="my-2 line-clamp-1 text-3xl font-bold text-background-fg"
        >
          {{ formattedTotal }}
        </p>
        <p class="text-sm text-background-fg/70">
          {{ rangeDescription }}
        </p>
      </div>

      <dl>
        <div class="flex items-center justify-between">
          <dt
            class="text-sm leading-loose text-background-fg/70"
          >
            Date
          </dt>
          <dd class="text-base font-semibold">
            {{ formatToDate(report.createdAt) }}
          </dd>
        </div>
        <div class="flex items-center justify-between">
          <dt
            class="text-sm leading-loose text-background-fg/70"
          >
            Time
          </dt>
          <dd class="text-base font-semibold">
            {{ formatToTime(report.createdAt) }}
          </dd>
        </div>
        <div class="flex items-center justify-between">
          <dt
            class="text-sm leading-loose text-background-fg/70"
          >
            Terminal Serial Number
          </dt>
          <dd class="text-base font-semibold">
            {{ report.deviceSerialNumber }}
          </dd>
        </div>
      </dl>

      <div class="my-2 h-px w-full bg-background-fg/8" />

      <dl>
        <ZReportSummaryLine
          label="Sales"
          :count="report.saleCount"
          :amount="report.saleAmount"
        />
        <ZReportSummaryLine
          label="Voids"
          :count="report.voidCount"
          :amount="report.voidAmount"
        />
        <ZReportSummaryLine
          label="Cashback"
          :count="report.cashbackCount"
          :amount="report.cashbackAmount"
        />
        <ZReportSummaryLine
          label="Tips"
          :count="report.gratuityCount"
          :amount="report.gratuityAmount"
        />
        <ZReportSummaryLine
          label="Refunds"
          :count="report.refundCount"
          :amount="report.refundAmount"
        />
        <ZReportSummaryLine
          label="Completions"
          :count="report.completionCount"
          :amount="report.completionAmount"
        />

        <div class="my-2 h-px w-full bg-background-fg/8" />

        <ZReportSummaryLine
          label="Total"
          :count="report.totalCount"
          :amount="report.totalAmount"
          emphasis
        />

        <template v-if="hasPreAuthRows">
          <div
            class="my-2 h-px w-full bg-background-fg/8"
          />
          <ZReportSummaryLine
            label="Pre-Auth"
            :count="report.preAuthCount"
            :amount="report.preAuthAmount"
          />
          <ZReportSummaryLine
            label="Pre-Auth Top-Up"
            :count="report.preAuthTopUpCount"
            :amount="report.preAuthTopUpAmount"
          />
          <ZReportSummaryLine
            label="Pre-Auth Voids"
            :count="report.preAuthCancelledCount"
            :amount="report.preAuthCancelledAmount"
          />
        </template>
      </dl>

      <template v-if="report.receipt">
        <HeadlessDisclosure
          #default="{ open: receiptIsOpen }"
        >
          <Transition
            enter-active-class="transition-all duration-300 overflow-hidden"
            enter-from-class="transform scale-95 opacity-0 max-h-0"
            enter-to-class="transform scale-100 opacity-100 max-h-[1000px]"
            leave-active-class="transition-all duration-200 overflow-hidden"
            leave-from-class="transform max-h-[1000px]"
            leave-to-class="transform max-h-0"
          >
            <HeadlessDisclosurePanel>
              <div
                class="flex flex-col items-center pb-4 pt-6"
              >
                <div class="w-full max-w-xs space-y-2">
                  <ReceiptPreview
                    :receipt-string="
                      stripTags(report.receipt)
                    "
                  />
                </div>
              </div>
            </HeadlessDisclosurePanel>
          </Transition>
          <HeadlessDisclosureButton
            :class="[
              'flex w-full items-center justify-center rounded-lg py-2',
              'hover:bg-background-fg/3',
            ]"
          >
            <template v-if="receiptIsOpen">
              <span> Hide Receipt </span>
              <IconChevronUp class="size-6" />
            </template>
            <template v-else>
              <span> Show Receipt </span>
              <IconChevronDown class="size-6" />
            </template>
          </HeadlessDisclosureButton>
        </HeadlessDisclosure>
      </template>
    </section>
  </template>

  <BaseInnerLoading v-else-if="isFetching" />

  <ErrorText v-else-if="isError">
    Failed to load Z report
  </ErrorText>
</template>

<script setup lang="ts">
import { useZReport } from "@/services/zReports/retrieveZReport"

const props = defineProps<{
  zReportId: string
}>()

const {
  data: report,
  isError,
  isFetching,
} = useZReport(props.zReportId)

const { formatToMerchantCurrencyString } = useCurrency()

const formattedTotal = computed(() =>
  report.value?.totalAmount == null
    ? "—"
    : formatToMerchantCurrencyString(
        report.value.totalAmount
      )
)

const rangeDescription = computed(() => {
  if (!report.value) return ""
  const { fromReceiptNo, toReceiptNo, totalCount } =
    report.value

  if (fromReceiptNo != null && toReceiptNo != null) {
    return `Receipts #${fromReceiptNo} – #${toReceiptNo}`
  }

  if (totalCount == null) return "Count unknown"
  if (totalCount === 0) return "No transactions"
  if (totalCount === 1) return "1 transaction"
  return `${totalCount} transactions`
})

const hasPreAuthRows = computed(() => {
  if (!report.value) return false
  return [
    report.value.preAuthCount,
    report.value.preAuthTopUpCount,
    report.value.preAuthCancelledCount,
  ].some((value) => value != null && value !== 0)
})

const stripTags = (html: string) =>
  html.replace(/<[^>]*>/g, "")
</script>
